import React, { useState, KeyboardEvent, useRef } from 'react';

export interface TagInputWidgetProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  strict?: boolean;
  placeholder?: string;
  className?: string;
}

export default function TagInputWidget({
  tags,
  onChange,
  suggestions = [],
  strict = false,
  placeholder = "Add a tag...",
  className = ""
}: TagInputWidgetProps) {
  const [inputVal, setInputVal] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    if (strict && !suggestions.includes(trimmed)) return;
    if (tags.includes(trimmed)) {
      setInputVal('');
      return;
    }
    onChange([...tags, trimmed]);
    setInputVal('');
  };

  const removeTag = (idx: number) => {
    const newTags = [...tags];
    newTags.splice(idx, 1);
    onChange(newTags);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputVal);
    } else if (e.key === 'Backspace' && !inputVal && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  const filteredSuggestions = suggestions.filter(
    s => s.toLowerCase().includes(inputVal.toLowerCase()) && !tags.includes(s)
  );

  return (
    <div className={`apexo-tag-input-container ${className}`}>
      <div 
        className={`apexo-tag-input-box ${isFocused ? 'focused' : ''}`}
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, i) => (
          <span key={i} className="apexo-tag">
            {tag}
            <button 
              className="apexo-tag-remove" 
              onClick={(e) => { e.stopPropagation(); removeTag(i); }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="apexo-tag-input"
        />
      </div>
      
      {isFocused && inputVal && filteredSuggestions.length > 0 && (
        <div className="apexo-tag-dropdown">
          {filteredSuggestions.map((s, i) => (
            <div 
              key={i} 
              className="apexo-tag-dropdown-item"
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
            >
              {s}
            </div>
          ))}
        </div>
      )}
      {isFocused && inputVal && strict && filteredSuggestions.length === 0 && (
        <div className="apexo-tag-dropdown">
          <div className="apexo-tag-dropdown-empty">No matching predefined tags.</div>
        </div>
      )}
    </div>
  );
}
